"use client";
// app/src/components/BankSelector.tsx
import React, { useState, useRef, FC } from "react";
import { useAppStore, selectUser } from "@/store/appStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { UserWithCompanies } from "@/components/auth";
import {} from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BankInstitution, Prisma } from "@prisma/client";

import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { debounce } from "lodash"; // Install lodash or use a custom debounce function
import { BankSelectorProps } from "./bank-selector.types";

// Define the InstitutionWithBankAccounts type
interface InstitutionWithBankAccounts extends BankInstitution {
  bankAccounts: Array<
    Prisma.BankAccountGetPayload<{ include: { institution: true } }>
  >;
}

const BankSelector: FC<BankSelectorProps> = ({ trigger }) => {
  const [searchResults, setSearchResults] = useState<
    InstitutionWithBankAccounts[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [open, setOpen] = React.useState(false);
  const user = useAppStore(selectUser);
  const searchValueRef = useRef<string>("");

  // group bank accounts by institution
  const bankAccountsByInstitution: InstitutionWithBankAccounts[] = (
    user?.selectedCompany?.bankAccounts || []
  ).reduce(
    (
      acc: InstitutionWithBankAccounts[],
      bankAccount: Prisma.BankAccountGetPayload<{
        include: { institution: true };
      }>
    ) => {
      let institution = acc.find(
        (i: InstitutionWithBankAccounts) => i.id === bankAccount.institutionId
      );
      if (!institution) {
        institution = { ...bankAccount.institution, bankAccounts: [] };
        acc.push(institution!);
      }
      institution!.bankAccounts.push(bankAccount);
      return acc;
    },
    []
  );

  const handleBankSearch = debounce(async (value) => {
    if (value) {
      const institutionsResponse = await fetch(
        `/api/institutions/search?name=${value}`
      );
      const institutionsList = await institutionsResponse.json();
      setSearchResults(institutionsList);
    } else {
      setSearchResults([]);
    }
    setIsSearching(false); // Update state after results are set
  }, 300); // Adjust debounce delay as needed

  const onBankSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value;
    searchValueRef.current = value;
    setIsSearching(true); // Set searching state
    handleBankSearch(value); // Debounce the search logic
  };

  const openBankConnection = (institutionId: string) => {
    // Step 1: Get Access Token
    fetch("/api/gocardless/access-token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.message || "Failed to get access token");
        }
        return res.json();
      })
      .then((tokenData) => {
        const accessToken = tokenData.access;
        if (!accessToken) throw new Error("No access token returned");
        // Step 2: Create User Agreement
        return fetch("/api/gocardless/user-agreement", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ institutionId, accessToken }),
        }).then(async (res) => {
          if (!res.ok) {
            const error = await res.json();
            throw new Error(error.message || "Failed to create user agreement");
          }
          return res.json().then((agreement) => ({ agreement, accessToken }));
        });
      })
      .then(({ agreement, accessToken }) => {
        // Step 3: Create Requisition
        const redirectUrl = window.location.origin + "/api/gocardless/callback";
        // Get selectedCompanyId from user query result
        const selectedCompanyId = user?.selectedCompany?.id;
        return fetch("/api/gocardless/requisition", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            institution_id: agreement.institution_id,
            agreement: agreement.id,
            accessToken,
            redirect: redirectUrl,
            user_language: "EN",
            selectedCompanyId,
            // reference removed: GoCardless will use its own requisition ID
          }),
        });
      })
      .then(async (res) => {
        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.message || "Failed to create requisition");
        }
        return res.json();
      })
      .then((requisition) => {
        // Redirect user to GoCardless authentication link
        if (requisition.link) {
          window.location.href = requisition.link;
        } else {
          throw new Error("No authentication link returned");
        }
        setOpen(false);
      })
      .catch((err) => {
        alert("Error: " + err.message);
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect a Bank Account</DialogTitle>
        </DialogHeader>

        <div className="framer max-w-[465px]">
          <Command
            shouldFilter={false}
            onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) =>
              onBankSearch(event)
            }
          >
            <div className="bg-gray-50 shadow-none rounded-sm border border-gray-300 mb-2">
              <CommandInput
                cmdk-input="true"
                autoFocus
                placeholder="Bank name..."
                variant="input"
              />
            </div>

            <CommandList cmdk-list="true">
              {isSearching ? (
                <CommandEmpty>Searching...</CommandEmpty>
              ) : searchResults.length ? (
                <CommandGroup className="p-0">
                  {searchResults.map(
                    (
                      institution: InstitutionWithBankAccounts,
                      index: number
                    ) => (
                      <CommandItem
                        className="cursor-pointer"
                        cmdk-item="true"
                        key={index}
                        value={institution.id}
                        onSelect={() =>
                          openBankConnection(institution.institutionId)
                        }
                      >
                        <Avatar className="institution-icon rounded-sm">
                          <AvatarImage src={institution.logo} />
                          <AvatarFallback>
                            {institution.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col ml-2">
                          <span className="institution-name">
                            {institution.name}
                          </span>
                          <span className="institution-country flex flex-wrap items-start justify-start">
                            {institution.countries.map(
                              (country: string, countryIndex: number) => (
                                <span
                                  className="border border-gray-300 rounded-sm px-1 text-xs mr-1 mb-1"
                                  key={countryIndex}
                                >
                                  {country}
                                </span>
                              )
                            )}
                          </span>
                        </div>
                      </CommandItem>
                    )
                  )}
                </CommandGroup>
              ) : (
                <CommandEmpty>
                  No banks found for {searchValueRef.current}.
                </CommandEmpty>
              )}
            </CommandList>
          </Command>
        </div>
      </DialogContent>

      <DialogTrigger asChild>{trigger}</DialogTrigger>
    </Dialog>
  );
};

export { BankSelector };
